/**
 * Hooks de chat · TanStack Query + Realtime invalidator.
 */
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useEffect } from 'react';
import { toast } from 'sonner';

import { markChannelChatNotificationsRead } from '@/features/notifications/api';
import { useSupabase } from '@/hooks/useSupabase';

import * as attachmentsApi from './api/attachments';
import * as channelsApi from './api/channels';
import * as channelPrefsApi from './api/channelPrefs';
import * as messagesApi from './api/messages';
import * as pinsApi from './api/pins';
import * as savedApi from './api/saved';
import * as usersApi from './api/users';
import { subscribeToChat, type ChatRealtimeEvent } from './realtime';

export const CHAT_KEYS = {
  channels: ['core', 'channels'] as const,
  channel: (id: string) => ['core', 'channels', id] as const,
  members: (channelId: string) => ['core', 'channel_members', channelId] as const,
  messages: (channelId: string) => ['core', 'messages', channelId] as const,
  thread: (rootId: string) => ['core', 'messages', 'thread', rootId] as const,
  reactions: (channelId: string) =>
    ['core', 'message_reactions', channelId] as const,
  myMentions: ['core', 'mentions', 'unread'] as const,
  usersBasic: (ids: string[]) =>
    ['core', 'users_basic', [...ids].sort().join(',')] as const,
  attachments: (ownerKind: string, ids: string[]) =>
    ['core', 'attachments', ownerKind, [...ids].sort().join(',')] as const,
  pins: (channelId: string) => ['core', 'pinned_messages', channelId] as const,
  saved: ['core', 'saved_messages'] as const,
  savedIds: ['core', 'saved_messages', 'ids'] as const,
  channelPref: (channelId: string) => ['core', 'channel_pref', channelId] as const,
  myThreadRoots: (userId: string) => ['core', 'my_thread_roots', userId] as const,
};

// =====================
// Channels
// =====================

export function useChannels() {
  return useQuery({
    queryKey: CHAT_KEYS.channels,
    queryFn: channelsApi.listChannels,
    staleTime: 30_000,
  });
}

export function useChannel(id: string | null) {
  return useQuery({
    queryKey: CHAT_KEYS.channel(id ?? ''),
    queryFn: () => channelsApi.getChannel(id!),
    enabled: !!id,
  });
}

export function useChannelMembers(channelId: string | null) {
  return useQuery({
    queryKey: CHAT_KEYS.members(channelId ?? ''),
    queryFn: () => channelsApi.listChannelMembers(channelId!),
    enabled: !!channelId,
  });
}

export function useCreateChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: channelsApi.createChannel,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: CHAT_KEYS.channels });
      toast.success('Canal creado');
    },
    onError: (error: Error) =>
      toast.error('No se pudo crear el canal', { description: error.message }),
  });
}

/**
 * Guarda los datos del canal. Invalida el listado además del canal: el nombre y
 * el archivado se ven en la barra lateral, no solo en el encabezado.
 */
export function useUpdateChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      patch,
    }: {
      id: string;
      patch: Parameters<typeof channelsApi.updateChannel>[1];
    }) => channelsApi.updateChannel(id, patch),
    onSuccess: (channel) => {
      qc.invalidateQueries({ queryKey: CHAT_KEYS.channels });
      qc.invalidateQueries({ queryKey: CHAT_KEYS.channel(channel.id) });
      toast.success('Canal actualizado');
    },
    onError: (e: Error) =>
      toast.error('No se pudo actualizar el canal', { description: e.message }),
  });
}

export function useAddChannelMembers(channelId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userIds: string[]) =>
      channelsApi.addChannelMembers(channelId, userIds),
    onSuccess: (_data, userIds) => {
      qc.invalidateQueries({ queryKey: CHAT_KEYS.members(channelId) });
      toast.success(
        userIds.length === 1 ? 'Persona agregada' : `${userIds.length} personas agregadas`,
      );
    },
    onError: (error: Error) =>
      toast.error('No se pudo agregar', { description: error.message }),
  });
}

export function useRemoveChannelMember(channelId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      channelsApi.removeChannelMember(channelId, userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: CHAT_KEYS.members(channelId) });
      toast.success('Persona quitada del canal');
    },
    onError: (error: Error) =>
      toast.error('No se pudo quitar', { description: error.message }),
  });
}

export function useMarkChannelRead() {
  return useMutation({
    mutationFn: ({ channelId, userId }: { channelId: string; userId: string }) =>
      channelsApi.markChannelRead(channelId, userId),
  });
}

/**
 * Al ENTRAR a un canal, limpia la campana de ese canal: marca leídas sus
 * menciones + notifs `chat_message` (se asume que el user leyó el chat) e
 * invalida ambas fuentes de la campana.
 */
export function useClearChannelBell() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (channelId: string) =>
      Promise.all([
        messagesApi.markChannelMentionsRead(channelId),
        markChannelChatNotificationsRead(channelId),
      ]).then(() => undefined),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: CHAT_KEYS.myMentions });
      qc.invalidateQueries({ queryKey: ['core', 'notifications', 'unread'] });
    },
  });
}

export function useGetOrCreateDm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (otherUserId: string) => channelsApi.getOrCreateDm(otherUserId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: CHAT_KEYS.channels });
    },
    onError: (error: Error) =>
      toast.error('No se pudo abrir el DM', { description: error.message }),
  });
}

// =====================
// Messages
// =====================

export function useMessages(channelId: string | null) {
  return useQuery({
    queryKey: CHAT_KEYS.messages(channelId ?? ''),
    queryFn: () => messagesApi.listMessages(channelId!),
    enabled: !!channelId,
    staleTime: 15_000,
  });
}

export function useSendMessage(channelId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: messagesApi.sendMessage,
    onSuccess: (newMessage) => {
      qc.invalidateQueries({ queryKey: CHAT_KEYS.messages(channelId) });
      qc.invalidateQueries({ queryKey: ['core', 'thread_counts', channelId] });
      // Si fue reply de un thread, también invalidar la query específica
      // del thread — sino el ThreadPanel no se refresca y obliga a F5.
      const rootId = newMessage?.thread_root_id;
      if (rootId) {
        qc.invalidateQueries({ queryKey: CHAT_KEYS.thread(rootId) });
        // Tu primer reply a un thread lo suma a la vista "Threads" del rail.
        qc.invalidateQueries({ queryKey: ['core', 'my_thread_roots'] });
      }
    },
    onError: (error: Error) =>
      toast.error('No se pudo enviar', { description: error.message }),
  });
}

export function useEditMessage(channelId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: string }) =>
      messagesApi.editMessage(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: CHAT_KEYS.messages(channelId) });
    },
    onError: (error: Error) =>
      toast.error('No se pudo editar', { description: error.message }),
  });
}

export function useDeleteMessage(channelId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => messagesApi.softDeleteMessage(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: CHAT_KEYS.messages(channelId) });
      toast.success('Mensaje eliminado');
    },
    onError: (error: Error) =>
      toast.error('No se pudo borrar', { description: error.message }),
  });
}

export function useThread(rootId: string | null) {
  return useQuery({
    queryKey: CHAT_KEYS.thread(rootId ?? ''),
    queryFn: () => messagesApi.listThread(rootId!),
    enabled: !!rootId,
  });
}

/**
 * Conteo de replies por thread root del canal activo.
 * Lo consume MessageList para mostrar el chip 'N respuestas' bajo cada msg raíz.
 * Se invalida con el mismo evento realtime que las messages.
 */
export function useThreadCounts(channelId: string | null) {
  return useQuery({
    queryKey: ['core', 'thread_counts', channelId ?? ''],
    queryFn: () => messagesApi.listThreadCounts(channelId!),
    enabled: !!channelId,
    staleTime: 15_000,
    select: (rows) => {
      const m = new Map<string, messagesApi.ThreadCount>();
      for (const r of rows) m.set(r.root_id, r);
      return m;
    },
  });
}

/**
 * Thread roots donde el usuario participó · vista "Threads" del rail.
 */
export function useMyThreadRoots(userId: string | null) {
  return useQuery({
    queryKey: CHAT_KEYS.myThreadRoots(userId ?? ''),
    queryFn: () => messagesApi.listMyThreadRoots(userId!),
    enabled: !!userId,
    staleTime: 30_000,
  });
}

// =====================
// Reactions
// =====================

export function useReactions(channelId: string | null, messageIds: string[]) {
  return useQuery({
    queryKey: [...CHAT_KEYS.reactions(channelId ?? ''), [...messageIds].sort().join(',')],
    queryFn: () => messagesApi.listReactions(messageIds),
    enabled: !!channelId && messageIds.length > 0,
    staleTime: 15_000,
    // El queryKey incluye la lista de messageIds: al entrar un mensaje nuevo la
    // key cambia y, sin esto, la query nueva devuelve [] mientras carga →
    // parpadeo. keepPreviousData mantiene las reacciones previas durante el
    // refetch (sin flash).
    placeholderData: keepPreviousData,
  });
}

export function useToggleReaction(channelId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ messageId, userId, emoji }: { messageId: string; userId: string; emoji: string }) =>
      messagesApi.toggleReaction(messageId, userId, emoji),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['core', 'message_reactions', channelId] });
    },
  });
}

// =====================
// Mentions
// =====================

export function useMyUnreadMentions() {
  return useQuery({
    queryKey: CHAT_KEYS.myMentions,
    queryFn: messagesApi.listMyUnreadMentions,
    staleTime: 60_000,
  });
}

export function useMarkMentionRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: messagesApi.markMentionRead,
    onSuccess: () => qc.invalidateQueries({ queryKey: CHAT_KEYS.myMentions }),
  });
}

export function useMarkAllMentionsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: messagesApi.markAllMentionsRead,
    onSuccess: () => qc.invalidateQueries({ queryKey: CHAT_KEYS.myMentions }),
  });
}

// =====================
// Users basic info (display_name + avatar)
// =====================

export function useUsersBasicInfo(userIds: string[]) {
  return useQuery({
    queryKey: CHAT_KEYS.usersBasic(userIds),
    queryFn: () => usersApi.getUsersBasicInfo(userIds),
    enabled: userIds.length > 0,
    staleTime: 5 * 60_000,
    // La clave cambia con cada lista de ids (otra página de /prospects, otro
    // canal). Sin esto el mapa queda vacío hasta que vuelve el RPC y toda la
    // página se ve sin nombres; las personas se repiten de una lista a otra,
    // así que el mapa anterior ya resuelve casi todas.
    placeholderData: keepPreviousData,
    select: (rows) => {
      const m = new Map<string, usersApi.UserBasicInfo>();
      for (const r of rows) m.set(r.user_id, r);
      return m;
    },
  });
}

// =====================
// Attachments
// =====================

export function useMessageAttachments(channelId: string | null, messageIds: string[]) {
  return useQuery({
    queryKey: CHAT_KEYS.attachments('message', messageIds),
    queryFn: () => attachmentsApi.listAttachmentsForOwners('message', messageIds),
    enabled: !!channelId && messageIds.length > 0,
    staleTime: 60_000,
    // El queryKey depende de la lista de messageIds. Al entrar un mensaje nuevo
    // la key cambia → sin esto la query devuelve [] mientras carga y los
    // <AttachmentPreview> se desmontan/re-montan (re-piden signed URL → las
    // imágenes recargan). keepPreviousData los mantiene montados.
    placeholderData: keepPreviousData,
  });
}

/**
 * URL firmada de un attachment, cacheada en React Query.
 *
 * Cada `createSignedUrl` devuelve un token distinto: si esto se pidiera en cada
 * montaje (como hacía el useEffect previo), un re-montaje del item cambiaba el
 * `src` del <img> y el browser re-descargaba. Cacheándola por `storagePath`, un
 * re-montaje reusa la MISMA URL → el browser sirve la imagen de su caché, sin
 * recargar. staleTime < expiry (1 h) para no servir una URL ya vencida.
 */
export function useSignedUrl(storagePath: string | null) {
  return useQuery({
    queryKey: ['core', 'attachment_signed_url', storagePath ?? ''],
    queryFn: () => attachmentsApi.getSignedUrl(storagePath!, 3600),
    enabled: !!storagePath,
    staleTime: 50 * 60_000, // 50 min (< 60 min de expiry)
    gcTime: 55 * 60_000,
  });
}

export function useUploadAttachment(channelId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: attachmentsApi.uploadAttachment,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['core', 'attachments'] });
      qc.invalidateQueries({ queryKey: CHAT_KEYS.messages(channelId) });
    },
    onError: (error: Error) =>
      toast.error('No se pudo subir el archivo', { description: error.message }),
  });
}

// =====================
// Pinned messages (Fase B·F2)
// =====================
//
// DEUDA conocida: core.pinned_messages NO está en la publication
// supabase_realtime, así que los fijados/desfijados de OTROS miembros no
// invalidan el contador ni el PinnedPanel hasta el próximo refetch (staleTime
// 30s). Las acciones propias sí refrescan (invalidación en las mutaciones).
// Para realtime cross-member haría falta: ALTER PUBLICATION supabase_realtime
// ADD TABLE core.pinned_messages + un kind 'pin' en realtime.ts + case en el
// invalidator. Impacto cosmético → se deja como deuda de Fase 2.

export function usePins(channelId: string | null) {
  return useQuery({
    queryKey: CHAT_KEYS.pins(channelId ?? ''),
    queryFn: () => pinsApi.listPins(channelId!),
    enabled: !!channelId,
    staleTime: 30_000,
  });
}

/** Fijados con el cuerpo del mensaje resuelto — para el PinnedPanel. */
export function usePinnedMessages(channelId: string | null) {
  return useQuery({
    queryKey: [...CHAT_KEYS.pins(channelId ?? ''), 'bodies'],
    queryFn: () => pinsApi.listPinnedWithBodies(channelId!),
    enabled: !!channelId,
    staleTime: 30_000,
  });
}

export function usePinMessage(channelId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (messageId: string) => pinsApi.pinMessage(channelId, messageId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: CHAT_KEYS.pins(channelId) });
      toast.success('Mensaje fijado');
    },
    onError: (error: Error) =>
      toast.error('No se pudo fijar', { description: error.message }),
  });
}

export function useUnpinMessage(channelId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (messageId: string) => pinsApi.unpinMessage(channelId, messageId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: CHAT_KEYS.pins(channelId) });
    },
    onError: (error: Error) =>
      toast.error('No se pudo des-fijar', { description: error.message }),
  });
}

// =====================
// Saved messages / bookmarks (Fase B·F2)
// =====================

export function useSavedMessages() {
  return useQuery({
    queryKey: CHAT_KEYS.saved,
    queryFn: savedApi.listSavedMessages,
    staleTime: 30_000,
  });
}

/** Set de message_ids guardados — para marcar el ícono en cada mensaje. */
export function useSavedMessageIds() {
  return useQuery({
    queryKey: CHAT_KEYS.savedIds,
    queryFn: savedApi.listSavedMessageIds,
    staleTime: 30_000,
    select: (ids) => new Set(ids),
  });
}

export function useSaveMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (messageId: string) => savedApi.saveMessage(messageId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: CHAT_KEYS.saved });
      qc.invalidateQueries({ queryKey: CHAT_KEYS.savedIds });
      toast.success('Guardado');
    },
    onError: (error: Error) =>
      toast.error('No se pudo guardar', { description: error.message }),
  });
}

export function useUnsaveMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (messageId: string) => savedApi.unsaveMessage(messageId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: CHAT_KEYS.saved });
      qc.invalidateQueries({ queryKey: CHAT_KEYS.savedIds });
    },
    onError: (error: Error) =>
      toast.error('No se pudo quitar de guardados', { description: error.message }),
  });
}

// =====================
// Channel prefs · silenciar / digest / salir (Fase 2 · sin tabla nueva)
// =====================

export function useMyChannelPref(channelId: string | null, userId: string | null) {
  return useQuery({
    queryKey: CHAT_KEYS.channelPref(channelId ?? ''),
    queryFn: () => channelPrefsApi.getMyChannelPref(channelId!, userId!),
    enabled: !!channelId && !!userId,
    staleTime: 30_000,
  });
}

export function useSetNotificationPref(channelId: string, userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (pref: channelPrefsApi.NotificationPref) =>
      channelPrefsApi.setNotificationPref(channelId, userId, pref),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: CHAT_KEYS.channelPref(channelId) });
      toast.success('Notificaciones actualizadas');
    },
    onError: (error: Error) =>
      toast.error('No se pudo actualizar', { description: error.message }),
  });
}

export function useSetChannelMuted(channelId: string, userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (mutedUntil: string | null) =>
      channelPrefsApi.setMuted(channelId, userId, mutedUntil),
    onSuccess: (_data, mutedUntil) => {
      qc.invalidateQueries({ queryKey: CHAT_KEYS.channelPref(channelId) });
      toast.success(mutedUntil ? 'Canal silenciado' : 'Canal reactivado');
    },
    onError: (error: Error) =>
      toast.error('No se pudo cambiar el silencio', { description: error.message }),
  });
}

export function useLeaveChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ channelId, userId }: { channelId: string; userId: string }) =>
      channelPrefsApi.leaveChannel(channelId, userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: CHAT_KEYS.channels });
      toast.success('Saliste del canal');
    },
    onError: (error: Error) =>
      toast.error('No se pudo salir del canal', { description: error.message }),
  });
}

// =====================
// Realtime invalidator
// =====================

export function useChatRealtimeInvalidator(): void {
  const qc = useQueryClient();
  const { session } = useSupabase();
  useEffect(() => {
    if (!session?.user?.id) return;
    const handle = (event: ChatRealtimeEvent) => {
      switch (event.kind) {
        case 'channel':
        case 'member':
          qc.invalidateQueries({ queryKey: CHAT_KEYS.channels });
          if (event.channelId) {
            qc.invalidateQueries({ queryKey: CHAT_KEYS.members(event.channelId) });
          }
          break;
        case 'message':
          if (event.channelId) {
            qc.invalidateQueries({ queryKey: CHAT_KEYS.messages(event.channelId) });
            qc.invalidateQueries({ queryKey: ['core', 'thread_counts', event.channelId] });
            // Broadcast a TODOS los threads abiertos — sin saber el root_id
            // del evento (el payload de realtime no lo expone), invalidamos
            // el prefix ['core', 'messages', 'thread'] que matchea cualquier
            // useThread(rootId) montado.
            qc.invalidateQueries({ queryKey: ['core', 'messages', 'thread'] });
          }
          break;
        case 'reaction':
          // Sin channel_id en payload — invalidamos por message
          qc.invalidateQueries({ queryKey: ['core', 'message_reactions'] });
          break;
        case 'mention':
          qc.invalidateQueries({ queryKey: CHAT_KEYS.myMentions });
          break;
      }
    };
    const channel = subscribeToChat(handle);
    return () => {
      void channel.unsubscribe();
    };
  }, [qc, session?.user?.id]);
}
